import { useCallback, useContext, useEffect, useState } from "react";
import {
  acceptResourceAllocationSettlement,
  getResourceAllocationFilterEmployees,
  getResourceAllocationSummary,
  rejectResourceAllocationSettlement,
} from "../services/resourceAllocation.service";
import { monthNames } from "../utils/consts";
import { isResourceAllocationReadOnly } from "../utils/resourceAllocationView";
import UserContext from "../modules/AppRouter/UserContext";
import type {
  IResourceAllocationEmployeeOptionApi,
  IResourceAllocationSummaryRowApi,
} from "../services/types/resourceAllocation";

export default function useResourceAllocationSummary(
  selectedMonth: string,
  selectedYear: number,
  selectedEmployeeId: string
) {
  const { userData } = useContext(UserContext);
  const workContextRole = userData?.data?.baseRole;

  const [rows, setRows] = useState<IResourceAllocationSummaryRowApi[]>([]);
  const [filterEmployees, setFilterEmployees] = useState<IResourceAllocationEmployeeOptionApi[]>([]);
  const [year, setYear] = useState(selectedYear);
  const [month, setMonth] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [readOnlyFromApi, setReadOnlyFromApi] = useState(false);

  const monthIndex = monthNames.indexOf(selectedMonth);
  const readOnlyMode = isResourceAllocationReadOnly(readOnlyFromApi, workContextRole);

  const fetchFilterEmployees = useCallback(async () => {
    const data = await getResourceAllocationFilterEmployees();
    setFilterEmployees(data);
  }, [workContextRole]);

  const fetchData = useCallback(async () => {
    if (monthIndex < 0) return;

    setRows([]);
    setIsLoading(true);
    try {
      const data = await getResourceAllocationSummary(
        selectedYear,
        monthIndex + 1,
        selectedEmployeeId || undefined
      );
      setRows(data.rows);
      setYear(data.year);
      setMonth(data.month);
      setReadOnlyFromApi(data.readOnlyMode);
    } catch (err) {
      console.error("❌ Błąd pobierania alokacji zasobów:", err);
      setRows([]);
    } finally {
      setIsLoading(false);
    }
  }, [monthIndex, selectedYear, selectedEmployeeId, workContextRole]);

  const acceptSettlement = async (row: IResourceAllocationSummaryRowApi) => {
    if (monthIndex < 0 || readOnlyMode) return;

    await acceptResourceAllocationSettlement(
      row.employeeId,
      selectedYear,
      monthIndex + 1
    );

    await fetchData();
  };

  const rejectSettlement = async (
    row: IResourceAllocationSummaryRowApi,
    rejectionReason: string
  ) => {
    if (monthIndex < 0 || readOnlyMode) return;

    await rejectResourceAllocationSettlement(
      row.employeeId,
      selectedYear,
      monthIndex + 1,
      rejectionReason
    );

    await fetchData();
  };

  useEffect(() => {
    fetchFilterEmployees();
  }, [fetchFilterEmployees]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    rows,
    filterEmployees,
    year,
    month,
    isLoading,
    readOnlyMode,
    refetch: fetchData,
    acceptSettlement: readOnlyMode ? undefined : acceptSettlement,
    rejectSettlement: readOnlyMode ? undefined : rejectSettlement,
    monthIndex,
  };
}
